import React from 'react'
import { useTranslation } from 'react-i18next'
import {Collapse as AntCollapse, Input as AntInput, Form, Radio} from 'antd'
import {observer} from 'mobx-react'
import {Collapse, Input, Select, MultiSelect} from '@/components'
import {OnlinePaymentsFilterFormProps} from './props'
import styles from './styles.scss'
import {
    ACCOUNT_EXPAND,
    ACCOUNT_ID, ACCOUNT_VALUE, AMOUNT_EXPAND,
    AMOUNT_FROM,
    AMOUNT_TO,
    CARD, CARD_EXPAND,
    CARD_MASK_FIRST,
    CARD_MASK_LAST,
    CARD_SCHEME, CARD_SCHEME_EXPAND,
    CURRENCY_EXPAND,
    CURRENCY_ID, DONATION_AMOUNT, DONATION_AMOUNT_FROM, DONATION_AMOUNT_TO, DONATION_EXPAND,
    PAYER_DESCRIPTION, PAYER_DESCRIPTION_EXPAND,
    PAYER_EMAIL, PAYER_EMAIL_EXPAND,
    PAYER_NAME, PAYER_NAME_EXPAND,
    PAYMENT_METHOD, PAYMENT_METHOD_EXPAND,
    PAYMENT_ORDER_NUMBER,
    PAYMENT_ORDER_NUMBER_EXPAND,
    POST_LINK_STATUS,
    POSTLINK_EXPAND,
    RRN, RRN_EXPAND,
    RRN_INPUT,
    STORE,
    STORE_EXPAND
} from '@/constants/playwright-ids'

const {Panel} = AntCollapse
const {Group: InputGroup} = AntInput

export const OnlinePaymentsFilterForm: React.FC<OnlinePaymentsFilterFormProps> = observer((
    {
        formId, form, onFinish, postLinkStatuses, paymentMethods, cardSchemes,
        currencies, stores, defaultActiveKey = ['invoice_id'],
        onFormValuesChange
    }
) => {
    const { t } = useTranslation()
    return (
        <Form
            id={formId}
            form={form}
            onValuesChange={(changedValues, allValues) => {
                onFormValuesChange(allValues)
            }}
            onFinish={onFinish}
        >
            <Collapse defaultActiveKey={[...defaultActiveKey]}>
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.orderNumber')}
                    key="invoice_id"
                    data-test-id={PAYMENT_ORDER_NUMBER_EXPAND}
                >
                    <Form.Item className={styles.formItem} name={'invoice_id'}>
                        <Input placeholder={t('OnlinePaymentsFilterForm.orderNumber')} data-test-id={PAYMENT_ORDER_NUMBER}/>
                    </Form.Item>
                </Panel>
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.description')}
                    key="description"
                    data-test-id={PAYER_DESCRIPTION_EXPAND}
                >
                    <Form.Item className={styles.formItem} name={'description'}>
                        <Input placeholder={t('OnlinePaymentsFilterForm.description')} data-test-id={PAYER_DESCRIPTION}/>
                    </Form.Item>
                </Panel>
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.customerName')}
                    key="payer_name"
                    data-test-id={PAYER_NAME_EXPAND}
                >
                    <Form.Item className={styles.formItem} name={'payer_name'}>
                        <Input placeholder={t('OnlinePaymentsFilterForm.customerName')} data-test-id={PAYER_NAME}/>
                    </Form.Item>
                </Panel>
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.email')}
                    key="payer_email"
                    data-test-id={PAYER_EMAIL_EXPAND}
                >
                    <Form.Item className={styles.formItem} name={'payer_email'}>
                        <Input placeholder={t('OnlinePaymentsFilterForm.email')} data-test-id={PAYER_EMAIL}/>
                    </Form.Item>
                </Panel>
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.amount.label')}
                    key="amount"
                    data-test-id={AMOUNT_EXPAND}
                >
                    <Form.Item className={styles.formItem} name={'amount'}>
                        <InputGroup compact>
                            <Form.Item name={['amount', 'from']} noStyle>
                                <Input style={{ width: '50%' }} placeholder={t('OnlinePaymentsFilterForm.amount.from')} data-test-id={AMOUNT_FROM} />
                            </Form.Item>
                            <Form.Item name={['amount', 'to']} noStyle>
                                <Input style={{ width: '50%' }} placeholder={t('OnlinePaymentsFilterForm.amount.to')} data-test-id={AMOUNT_TO} />
                            </Form.Item>
                        </InputGroup>
                    </Form.Item>
                </Panel>
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.currency')}
                    key="currency_id"
                    data-test-id={CURRENCY_EXPAND}
                >
                    <Form.Item className={styles.formItem} name={'currency_id'} data-test-id={CURRENCY_ID}>
                        <Select
                            placeholder={t('OnlinePaymentsFilterForm.currency')}
                            options={currencies}
                        />
                    </Form.Item>
                </Panel>
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.paymentMethod')}
                    key="payment_method"
                    data-test-id={PAYMENT_METHOD_EXPAND}
                >
                    <Form.Item
                        className={styles.formItem}
                        name={'payment_method'}
                        data-test-id={PAYMENT_METHOD}
                    >
                        <Select
                            placeholder={t('OnlinePaymentsFilterForm.paymentMethod')}
                            options={paymentMethods}
                        />
                    </Form.Item>
                </Panel>

                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.cardMask.label')}
                    key="card_mask"
                    data-test-id={CARD_EXPAND}
                >
                    <Form.Item className={styles.formItem} name={'card_mask'} data-test-id={CARD}>
                        <InputGroup compact>
                            <Form.Item name={['card_mask', 'from']} noStyle>
                                <Input style={{ width: '50%' }} placeholder={t('OnlinePaymentsFilterForm.cardMask.first')} data-test-id={CARD_MASK_FIRST} />
                            </Form.Item>
                            <Form.Item name={['card_mask', 'to']} noStyle>
                                <Input style={{ width: '50%' }} placeholder={t('OnlinePaymentsFilterForm.cardMask.last')} data-test-id={CARD_MASK_LAST} />
                            </Form.Item>
                        </InputGroup>
                    </Form.Item>
                </Panel>

                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.cardScheme')}
                    key={'card_type_id'}
                    data-test-id={CARD_SCHEME_EXPAND}
                >
                    <Form.Item
                        className={styles.formItem}
                        name={'card_type_id'}
                        data-test-id={CARD_SCHEME}
                    >
                        <MultiSelect
                            placeholder={t('OnlinePaymentsFilterForm.cardScheme')}
                            options={cardSchemes}
                            data-test-id={cardSchemes}
                        />
                    </Form.Item>
                </Panel>
                { stores?.length > 1 && (
                    <Panel
                        className={styles.panel}
                        header={t('OnlinePaymentsFilterForm.shop')}
                        key="shop"
                        data-test-id={STORE_EXPAND}
                    >
                        <Form.Item className={styles.formItem} name={'shop'} data-test-id={STORE}>
                            <Select
                                placeholder={t('OnlinePaymentsFilterForm.shopName')}
                                options={stores}
                            />
                        </Form.Item>
                    </Panel>
                ) }
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.customerId')}
                    key="account_id"
                    data-test-id={ACCOUNT_EXPAND}
                >
                    <Form.Item className={styles.formItem} name={'account_id'} data-test-id={ACCOUNT_ID}>
                        <Input placeholder={t('OnlinePaymentsFilterForm.customerId')} data-test-id={ACCOUNT_VALUE} />
                    </Form.Item>
                </Panel>
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.rrn')}
                    key="reference"
                    data-test-id={RRN_EXPAND}
                >
                    <Form.Item className={styles.formItem} name={'reference'} data-test-id={RRN}>
                        <Input placeholder={t('OnlinePaymentsFilterForm.receivalReferenceNumber')} data-test-id={RRN_INPUT} />
                    </Form.Item>
                </Panel>
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.postLink')}
                    key="post_link_status"
                    data-test-id={POSTLINK_EXPAND}
                >
                    <Form.Item
                        className={styles.formItem}
                        name={'post_link_status'}
                        data-test-id={POST_LINK_STATUS}
                    >
                        <Radio.Group buttonStyle="solid">
                            {postLinkStatuses.map(item =>
                                <Radio.Button
                                    data-test-id={item.label}
                                    key={`postlink_option_${item.value}`}
                                    value={item.value}>{item.label}
                                </Radio.Button>
                            )}
                        </Radio.Group>
                    </Form.Item>
                </Panel>
                <Panel
                    className={styles.panel}
                    header={t('OnlinePaymentsFilterForm.donationAmount.label')}
                    key="11"
                    data-test-id={DONATION_EXPAND}
                >
                    <Form.Item
                        className={styles.formItem}
                        name={'donation_amount'}
                        data-test-id={DONATION_AMOUNT}
                    >
                        <InputGroup compact>
                            <Form.Item name={['donation_amount', 'from']} noStyle>
                                <Input
                                    style={{ width: '50%' }}
                                    placeholder={t('OnlinePaymentsFilterForm.donationAmount.from')}
                                    data-test-id={DONATION_AMOUNT_FROM}
                                />
                            </Form.Item>
                            <Form.Item name={['donation_amount', 'to']} noStyle>
                                <Input
                                    style={{ width: '50%' }}
                                    placeholder={t('OnlinePaymentsFilterForm.donationAmount.to')}
                                    data-test-id={DONATION_AMOUNT_TO}
                                />
                            </Form.Item>
                        </InputGroup>
                    </Form.Item>
                </Panel>
            </Collapse>
        </Form>
    )
})
